import React, { useState } from 'react';

import { Box, HStack, Header, Link, addWithConfig } from '@rbilabs/universal-components';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { withTypography } from 'ucl/components/text/Typography';

import { IDeliveryDriver } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import CloseButton from 'components/close-button';
import CourierMarkerSvg from 'pages/order-confirmation/delivery/icons/courier-image';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

const Main = HStack.withConfig({
  marginBottom: spacing.$3_5,
  justifyContent: 'space-between',
  alignItems: 'center',
});

const Heading = Header.withConfig({
  variant: 'headerFour',
  color: primitive.$bbqBrown,
  fontSize: 'xl',
  lineHeight: 'md',
  margin: 0,
  marginBottom: '$4',
  textTransform: 'capitalize',
});

const CourierInfoContainer = HStack.withConfig({});

const CourierImg = Box.withConfig({
  alignItems: 'center',
  marginRight: '$2',
  borderRadius: 'full',
});

const CourierInfo = Box.withConfig({
  justifyContent: 'center',
  _text: {
    color: primitive.$greyOne,
  },
});

const CourierName = withTypography({
  fontSize: 'sm',
  mr: '$2',
  numberOfLines: 1,
  style: {
    maxWidth: 80,
  },
});

const CloseButtonWrapper = Box.withConfig({
  width: '$12',
  height: '$12',
  alignItems: 'center',
  _web: {
    borderRadius: '50%',
    safeAreaY: 0,
  },
});

// TODO: uncomment once make and model become available
// const VehicleInfo = Text.withConfig({
//   fontSize: 'xs',
// });

const ContactCourier = Box;

const CourierContactInfoContainer = HStack.withConfig({
  justifyContent: 'space-between',
  borderTopWidth: 1,
  borderTopColor: primitive.$blackOpacity10,
  paddingTop: '$2',
});

const CourierContactInfo = Box;

const ContactPrompt = withTypography({ fontSize: 'xs', color: primitive.$greyOne });

const ContactInfo = Link.withConfig({
  fontSize: 'sm',
  color: primitive.$greyOne,
  isUnderlined: false,
});

const ContactButton = addWithConfig(ActionButton).withConfig({
  maxWidth: '$32',
});

interface ICourierDetailProps {
  driver: IDeliveryDriver;
}

const CourierDetail: React.FC<React.PropsWithChildren<ICourierDetailProps>> = ({ driver }) => {
  const [isContactVisible, setIsContactVisible] = useState<boolean>(false);

  return (
    <>
      <Heading>{t('yourCourier')}</Heading>
      <Main>
        <CourierInfoContainer>
          <CourierImg testID="driver-img">
            {/* TODO: RN - Figure out how we want to handle image url sources not from sanity */}
            {/* <Picture image={driver?.profileImageUrl} alt="courier" /> */}
            <CourierMarkerSvg />
          </CourierImg>
          <CourierInfo>
            <DatadogSessionReplayMask>
              <CourierName testID="driver-name" data-private>
                {driver?.name || t('noCourierName')}
              </CourierName>
            </DatadogSessionReplayMask>
            {/* {TODO: uncomment this line once make and model become available} */}
            {/* <VehicleInfo>Make & Model</VehicleInfo> */}
          </CourierInfo>
        </CourierInfoContainer>
        <ContactCourier>
          <ContactButton
            testID="driver-contact"
            variant={ActionButtonVariants.OUTLINE}
            fullWidth
            disabled={isContactVisible}
            onPress={() => setIsContactVisible(true)}
          >
            {t('contact')}
          </ContactButton>
        </ContactCourier>
      </Main>
      {isContactVisible && (
        <CourierContactInfoContainer>
          <CourierContactInfo>
            <ContactPrompt>{t('contactCourier')}</ContactPrompt>
            <DatadogSessionReplayMask>
              <ContactInfo href={`tel:${driver?.phoneNumber}`} testID="driver-phone" data-private>
                {driver?.phoneNumber || t('noCourierPhone')}
              </ContactInfo>
            </DatadogSessionReplayMask>
          </CourierContactInfo>
          <CloseButtonWrapper>
            <CloseButton onPress={() => setIsContactVisible(false)} testID="driver-contact-close" />
          </CloseButtonWrapper>
        </CourierContactInfoContainer>
      )}
    </>
  );
};

export default CourierDetail;
